import React, { useState, useRef, useEffect } from "react";

/**
 * Reusable country selector.
 * On selection, calls onChange({ name, isoCode, dialingCode, flag })
 * Supports keyboard navigation and search.
 */

export const COUNTRIES = [
  { name: "India",                isoCode: "IN",  dialingCode: "+91",   flag: "🇮🇳" },
  { name: "Kenya",                isoCode: "KE",  dialingCode: "+254",  flag: "🇰🇪" },
  { name: "United States",        isoCode: "US",  dialingCode: "+1",    flag: "🇺🇸" },
  { name: "United Kingdom",       isoCode: "GB",  dialingCode: "+44",   flag: "🇬🇧" },
  { name: "United Arab Emirates", isoCode: "AE",  dialingCode: "+971",  flag: "🇦🇪" },
  { name: "Saudi Arabia",         isoCode: "SA",  dialingCode: "+966",  flag: "🇸🇦" },
  { name: "Singapore",            isoCode: "SG",  dialingCode: "+65",   flag: "🇸🇬" },
  { name: "Australia",            isoCode: "AU",  dialingCode: "+61",   flag: "🇦🇺" },
  { name: "Canada",               isoCode: "CA",  dialingCode: "+1",    flag: "🇨🇦" },
  { name: "Germany",              isoCode: "DE",  dialingCode: "+49",   flag: "🇩🇪" },
  { name: "France",               isoCode: "FR",  dialingCode: "+33",   flag: "🇫🇷" },
  { name: "Japan",                isoCode: "JP",  dialingCode: "+81",   flag: "🇯🇵" },
  { name: "Nigeria",              isoCode: "NG",  dialingCode: "+234",  flag: "🇳🇬" },
  { name: "Tanzania",             isoCode: "TZ",  dialingCode: "+255",  flag: "🇹🇿" },
  { name: "Uganda",               isoCode: "UG",  dialingCode: "+256",  flag: "🇺🇬" },
  { name: "South Africa",         isoCode: "ZA",  dialingCode: "+27",   flag: "🇿🇦" },
  { name: "Ethiopia",             isoCode: "ET",  dialingCode: "+251",  flag: "🇪🇹" },
  { name: "Ghana",                isoCode: "GH",  dialingCode: "+233",  flag: "🇬🇭" },
  { name: "Rwanda",               isoCode: "RW",  dialingCode: "+250",  flag: "🇷🇼" },
  { name: "Pakistan",             isoCode: "PK",  dialingCode: "+92",   flag: "🇵🇰" },
  { name: "Bangladesh",           isoCode: "BD",  dialingCode: "+880",  flag: "🇧🇩" },
  { name: "Sri Lanka",            isoCode: "LK",  dialingCode: "+94",   flag: "🇱🇰" },
  { name: "Nepal",                isoCode: "NP",  dialingCode: "+977",  flag: "🇳🇵" },
  { name: "China",                isoCode: "CN",  dialingCode: "+86",   flag: "🇨🇳" },
];

/** Find a country by ISO code, dialing code, or name (fuzzy). */
export function findCountry(value) {
  if (!value) return null;
  const v = value.trim();
  return (
    COUNTRIES.find((c) => c.isoCode === v.toUpperCase()) ||
    COUNTRIES.find((c) => c.dialingCode === v) ||
    COUNTRIES.find((c) => c.name.toLowerCase() === v.toLowerCase()) ||
    null
  );
}

export default function CountrySelector({
  value,           // current ISO code (preferred), country name, or dialing code
  onChange,        // fn({ name, isoCode, dialingCode, flag })
  disabled = false,
  required = false,
  hasError = false,
  placeholder = "Select Country",
  className = "",
}) {
  const selected = findCountry(value);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  const filtered = COUNTRIES.filter((c) =>
    `${c.name} ${c.isoCode} ${c.dialingCode}`.toLowerCase().includes(search.toLowerCase())
  );

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSelect = (country) => {
    onChange(country);
    setOpen(false);
    setSearch("");
  };

  return (
    <div
      ref={wrapperRef}
      className={`country-selector-wrapper ${className} ${hasError ? "invalid" : ""} ${disabled ? "disabled" : ""}`}
      style={{ position: "relative" }}
    >
      {/* Trigger */}
      <button
        type="button"
        className={`country-selector-trigger form-control ${hasError ? "invalid" : ""}`}
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          textAlign: "left",
          width: "100%",
          background: disabled ? "#f9fafb" : "#fff",
          cursor: disabled ? "not-allowed" : "pointer",
          border: hasError ? "1px solid #ef4444" : "1px solid #d1d5db",
          borderRadius: "6px",
          padding: "8px 12px",
          fontSize: "0.875rem",
          color: selected ? "#1e293b" : "#9ca3af",
        }}
      >
        {selected ? (
          <>
            <span style={{ fontSize: "1.1rem" }}>{selected.flag}</span>
            <span>{selected.name}</span>
            <span style={{ marginLeft: "auto", color: "#6b7280", fontSize: "0.8rem" }}>
              {selected.isoCode} · {selected.dialingCode}
            </span>
          </>
        ) : (
          <span>{placeholder}{required ? " *" : ""}</span>
        )}
        <span style={{ marginLeft: selected ? "4px" : "auto", color: "#9ca3af", fontSize: "0.7rem" }}>▼</span>
      </button>

      {/* Dropdown */}
      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            background: "#fff",
            border: "1px solid #d1d5db",
            borderRadius: "8px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            zIndex: 1000,
            overflow: "hidden",
          }}
        >
          {/* Search */}
          <div style={{ padding: "8px", borderBottom: "1px solid #f3f4f6" }}>
            <input
              autoFocus
              type="text"
              placeholder="Search country…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: "100%",
                padding: "6px 10px",
                border: "1px solid #e5e7eb",
                borderRadius: "4px",
                fontSize: "0.85rem",
                outline: "none",
              }}
            />
          </div>

          {/* Options */}
          <div style={{ maxHeight: "220px", overflowY: "auto" }}>
            {filtered.length === 0 ? (
              <div style={{ padding: "12px", color: "#9ca3af", fontSize: "0.85rem", textAlign: "center" }}>
                No countries found
              </div>
            ) : (
              filtered.map((country) => (
                <div
                  key={country.isoCode}
                  onClick={() => handleSelect(country)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    padding: "9px 12px",
                    cursor: "pointer",
                    fontSize: "0.875rem",
                    backgroundColor: selected?.isoCode === country.isoCode ? "#fff5f2" : "transparent",
                    borderLeft: selected?.isoCode === country.isoCode ? "3px solid #FF683A" : "3px solid transparent",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f9fafb"}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = selected?.isoCode === country.isoCode ? "#fff5f2" : "transparent"}
                >
                  <span style={{ fontSize: "1.1rem" }}>{country.flag}</span>
                  <span style={{ flex: 1, color: "#1e293b" }}>{country.name}</span>
                  <span style={{ color: "#6b7280", fontSize: "0.78rem", whiteSpace: "nowrap" }}>
                    {country.isoCode} · {country.dialingCode}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
